import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	edit_05,
	heart,
	heartSolid,
	searchSmLine,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";

import { UserDefaultCell } from "~/features/address-book/components/user-default-cell/user-default-cell.tsx";
import {
	AddressBookCard,
	AddressBookCardSkeleton,
	addressBookCardStyles,
} from "~/shared/components/address-book-card/address-book-card.tsx";
import { useSearchableInfiniteList } from "~/shared/hooks/use-searchable-infinite-list.ts";
import type { AddressBookItemWithFavorite } from "~/shared/schemas/address-book-schema.ts";

import styles from "./mobile-address-book.module.css";

type MobileAddressBookProps = {
	addresses: AddressBookItemWithFavorite[];
	loading: boolean;
	onEdit: (addressBookItemId: number) => void;
	onFavorite: (item: AddressBookItemWithFavorite) => void;
	onDelete: (addressBookItemId: number, companyName: string) => void;
	onSetUserDefault: (
		addressBookItemId: number,
		type: "Pickup" | "Delivery",
	) => void;
};

const LOADING_CARD_COUNT = 6;

const buildCityStateZip = (item: AddressBookItemWithFavorite) =>
	[item.city, [item.stateProvince, item.postalCode].filter(Boolean).join(" ")]
		.filter(Boolean)
		.join(", ");

const getSearchableFields = (item: AddressBookItemWithFavorite) => [
	item.companyName,
	item.address1,
	item.address2,
	item.city,
	item.stateProvince,
	item.postalCode,
	item.pickupContactName,
	item.deliveryContactName,
];

export const MobileAddressBook = ({
	addresses,
	loading,
	onEdit,
	onFavorite,
	onDelete,
	onSetUserDefault,
}: MobileAddressBookProps) => {
	const {
		searchTerm,
		setSearchTerm,
		visibleItems: visibleAddresses,
		filteredCount,
		handleScroll,
	} = useSearchableInfiniteList({
		items: addresses,
		getSearchableFields,
		pageSize: 25,
	});

	const handleSearchChange = (event: InputChangeEvent) => {
		setSearchTerm(String(event.value ?? ""));
	};

	return (
		<div className={styles.container}>
			<div className={styles.searchWrapper}>
				<Input
					aria-label={"Search addresses"}
					className={styles.searchInput}
					data-testid={"address-search-input"}
					disabled={loading}
					onChange={handleSearchChange}
					placeholder={"Search addresses..."}
					value={searchTerm}
				/>
				<SvgIcon className={styles.searchIcon} icon={searchSmLine} />
			</div>
			<div
				aria-busy={loading}
				className={styles.listScrollRegion}
				data-testid={"address-list-scroll-region"}
				onScroll={handleScroll}
			>
				{loading ? (
					Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
						<AddressBookCardSkeleton key={`loading-${index}`} />
					))
				) : (
					<>
						{filteredCount === 0 ? (
							<div className={styles.emptyMessage}>No matching addresses</div>
						) : null}
						{visibleAddresses.map((item) => {
							const hasPickupDetail = item.details.some(
								(detail) => detail.stopType === 1,
							);
							const hasDeliveryDetail = item.details.some(
								(detail) => detail.stopType === 2,
							);
							const recordName = item.companyName || "address";
							return (
								<AddressBookCard
									key={item.addressBookItemId}
									actions={
										<>
											<UserDefaultCell
												hasDeliveryDetail={hasDeliveryDetail}
												hasPickupDetail={hasPickupDetail}
												isDeliveryDefault={item.isDeliveryDefault}
												isPickupDefault={item.isPickupDefault}
												onSetUserDefault={(type) => {
													onSetUserDefault(item.addressBookItemId, type);
												}}
											/>
											<Button
												aria-label={`Edit ${recordName} address`}
												data-testid={"address-card-edit-button"}
												onClick={() => {
													onEdit(item.addressBookItemId);
												}}
												svgIcon={edit_05}
												variant={"tertiary"}
											/>
											<Button
												aria-label={`${item.isFavorite ? "Unfavorite" : "Favorite"} ${recordName} address`}
												data-testid={"address-card-favorite-button"}
												onClick={() => {
													onFavorite(item);
												}}
												svgIcon={item.isFavorite ? heartSolid : heart}
												variant={"tertiary"}
											/>
											<Button
												aria-label={`Delete ${recordName} address`}
												data-testid={"address-card-delete-button"}
												disabled={item.isLegacy}
												onClick={() => {
													onDelete(
														item.addressBookItemId,
														item.companyName ?? "",
													);
												}}
												svgIcon={trash_01}
												variant={"dangerTertiary"}
											/>
										</>
									}
									title={
										<>
											{item.companyName}
											{item.isPickupDefault || item.isDeliveryDefault ? (
												<span className={styles.defaultBadge}>Default</span>
											) : null}
										</>
									}
								>
									<div className={styles.cardBody}>
										<div className={addressBookCardStyles.fieldGroup}>
											<div className={addressBookCardStyles.fieldLabel}>
												Address
											</div>
											<div data-address-lines={true}>
												<div>{item.address1}</div>
												{item.address2 ? <div>{item.address2}</div> : null}
												<div>{buildCityStateZip(item)}</div>
											</div>
										</div>
										<div className={addressBookCardStyles.fieldGroup}>
											{item.pickupContactName ? (
												<>
													<div className={addressBookCardStyles.fieldLabel}>
														Pickup Contact
													</div>
													<div>{item.pickupContactName}</div>
												</>
											) : null}
											{item.deliveryContactName ? (
												<>
													<div className={addressBookCardStyles.fieldLabel}>
														Delivery Contact
													</div>
													<div>{item.deliveryContactName}</div>
												</>
											) : null}
										</div>
									</div>
								</AddressBookCard>
							);
						})}
					</>
				)}
			</div>
		</div>
	);
};
